import { api } from './api';
import { resolveApiBaseUrl } from '../utils/apiBaseUrl';
import { getAccessToken } from '../utils/authToken';
import { User, UserListResponse, UserStats, AdminUserCreate, AdminUserUpdate, DemoUserCreate } from '../types/user';
import { CrisisCommunicationUser } from '../types/crisisCommunicationUser';

class AdminService {
  private baseUrl = '/v1/admin';

  async getUsers(params: {
    page?: number;
    per_page?: number;
    search?: string;
    role?: 'USER' | 'ADMIN' | 'SUPER_ADMIN' | 'INSURER';
    is_active?: boolean;
    insurer_org_id?: number;
  } = {}): Promise<UserListResponse> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.per_page) searchParams.append('per_page', params.per_page.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.role) searchParams.append('role', params.role);
    if (params.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());
    if (params.insurer_org_id !== undefined) {
      searchParams.append('insurer_org_id', params.insurer_org_id.toString());
    }

    const response = await api.get(`${this.baseUrl}/users?${searchParams.toString()}`);
    return response.data;
  }

  async getUser(userId: number): Promise<User> {
    const response = await api.get(`${this.baseUrl}/users/${userId}`);
    return response.data;
  }

  async createUser(userData: AdminUserCreate): Promise<User> {
    const response = await api.post(`${this.baseUrl}/users`, userData);
    return response.data;
  }

  async createDemoUser(demoData: DemoUserCreate): Promise<any> {
    const response = await api.post(`${this.baseUrl}/demo-users`, demoData);
    return response.data;
  }

  async updateUser(userId: number, userData: AdminUserUpdate): Promise<User> {
    const response = await api.put(`${this.baseUrl}/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: number): Promise<{ message: string }> {
    const response = await api.delete(`${this.baseUrl}/users/${userId}`);
    return response.data;
  }

  async activateUser(userId: number): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/users/${userId}/activate`);
    return response.data;
  }

  async deactivateUser(userId: number): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/users/${userId}/deactivate`);
    return response.data;
  }

  async resetUserPassword(userId: number, newPassword: string): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/users/${userId}/reset-password`, {
      new_password: newPassword
    });
    return response.data;
  }

  async getUserStats(): Promise<UserStats> {
    const response = await api.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  async getUserCrisisCommunications(userId: number): Promise<CrisisCommunicationUser[]> {
    const response = await api.get(`${this.baseUrl}/users/${userId}/crisis-communications`);
    return response.data;
  }

  // Helper methods
  getRoleDisplayName(role: string): string {
    switch (role) {
      case 'USER':
        return 'User';
      case 'ADMIN':
        return 'Admin';
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'INSURER':
        return 'Insurer';
      default:
        return role;
    }
  }

  getRoleBadgeColor(role: string): string {
    switch (role) {
      case 'USER':
        return 'bg-gray-100 text-gray-800';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800';
      case 'SUPER_ADMIN':
        return 'bg-red-100 text-red-800';
      case 'INSURER':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  isUserLocked(user: User): boolean {
    if (!user.locked_until) return false;
    return new Date(user.locked_until) > new Date();
  }

  async listInsurerOrgs(status?: string): Promise<InsurerOrgListResponse> {
    const params = status ? `?status=${status}` : '';
    const response = await api.get(`${this.baseUrl}/insurer-orgs${params}`);
    return response.data;
  }

  async createInsurerOrg(data: InsurerOrgCreateRequest): Promise<InsurerOrgCreateResponse> {
    const response = await api.post(`${this.baseUrl}/insurer-orgs`, data);
    return response.data;
  }

  async approveInsurerOrg(orgId: number): Promise<InsurerOrg> {
    const response = await api.patch(`${this.baseUrl}/insurer-orgs/${orgId}/approve`);
    return response.data;
  }

  async updateInsurerOrgStatus(orgId: number, status: string): Promise<InsurerOrg> {
    const response = await api.patch(`${this.baseUrl}/insurer-orgs/${orgId}/status`, { status });
    return response.data;
  }

  async rejectInsurerOrg(orgId: number, reason?: string): Promise<InsurerOrg> {
    const response = await api.patch(`${this.baseUrl}/insurer-orgs/${orgId}/reject`, { reason });
    return response.data;
  }

  async listPartnerLedger(
    insurerOrgId?: number,
    fromDate?: string,
    toDate?: string
  ): Promise<{ entries: PartnerLedgerAdminEntry[]; total: number }> {
    const params = new URLSearchParams();
    if (insurerOrgId) params.append('insurer_org_id', String(insurerOrgId));
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`${this.baseUrl}/partner-ledger${qs}`);
    return response.data;
  }

  async updatePartnerLedgerStatus(
    ledgerId: number,
    data: { status?: string; invoice_reference?: string; note?: string }
  ): Promise<{ message: string; invoice_reference?: string | null }> {
    const response = await api.patch(`${this.baseUrl}/partner-ledger/${ledgerId}`, data);
    return response.data;
  }

  async getInsurerOrgDetail(orgId: number): Promise<InsurerOrgDetail> {
    const response = await api.get(`${this.baseUrl}/insurer-orgs/${orgId}`);
    return response.data;
  }

  async grantClientCredit(orgId: number, clientId: number): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/insurer-orgs/${orgId}/clients/${clientId}/grant-credit`);
    return response.data;
  }

  async revokeClientCredit(orgId: number, clientId: number): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/insurer-orgs/${orgId}/clients/${clientId}/revoke-credit`);
    return response.data;
  }

  async patchInsurerOrg(
    orgId: number,
    data: { name?: string; billing_email?: string; invite_contact_email?: string; mfa_policy?: 'optional' | 'mandatory' }
  ): Promise<InsurerOrg> {
    const response = await api.patch(`${this.baseUrl}/insurer-orgs/${orgId}`, data);
    return response.data;
  }

  async getPartnerMetrics(): Promise<{
    pending_insurer_orgs: number;
    active_insurer_orgs: number;
    outstanding_ledger_cents: number;
    credits_granted_mtd: number;
    audit_requests_mtd: number;
    simple_tokens_mtd: number;
    gold_tokens_mtd: number;
    premium_tokens_mtd: number;
  }> {
    const response = await api.get(`${this.baseUrl}/partner-metrics`);
    return response.data;
  }

  downloadOrgClientsCsv(orgId: number): void {
    this.downloadCsv(`${this.baseUrl}/insurer-orgs/${orgId}/clients/export`, `insurer-${orgId}-clients.csv`);
  }

  downloadPartnerLedgerCsv(insurerOrgId?: number, fromDate?: string, toDate?: string): void {
    const params = new URLSearchParams();
    if (insurerOrgId) params.append('insurer_org_id', String(insurerOrgId));
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const filename = insurerOrgId ? `partner-ledger-org-${insurerOrgId}.csv` : 'partner-ledger-all.csv';
    this.downloadCsv(`${this.baseUrl}/partner-ledger/export${qs}`, filename);
  }

  private downloadCsv(url: string, filename: string): void {
    const token = getAccessToken();
    const base = resolveApiBaseUrl(process.env.REACT_APP_API_URL).replace(/\/$/, '');
    fetch(`${base}${url}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Export failed');
        return r.blob();
      })
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      });
  }
}

export const adminService = new AdminService();

export interface InsurerOrg {
  id: number;
  slug: string;
  name: string;
  status: string;
  billing_email?: string | null;
  invite_contact_email?: string | null;
  has_logo?: boolean;
  mfa_policy?: 'optional' | 'mandatory';
  created_at: string;
  approved_at?: string | null;
}

export interface InsurerOrgListResponse {
  insurers: InsurerOrg[];
  total: number;
}

export interface InsurerOrgCreateRequest {
  org_name: string;
  billing_email: string;
  admin_email: string;
  admin_password?: string;
  admin_full_name?: string;
  auto_approve?: boolean;
  use_magic_link?: boolean;
}

export interface InsurerOrgCreateResponse {
  org: InsurerOrg;
  admin_user_id: number;
  admin_email: string;
}

export interface PartnerLedgerAdminEntry {
  id: number;
  insurer_org_id: number;
  insurer_name: string;
  client_email: string;
  client_name?: string | null;
  amount_cents: number;
  status: string;
  entry_type?: string | null;
  invoice_reference?: string | null;
  created_at: string;
  purchase_id: number;
  partner_token_tier?: 'simple' | 'gold' | 'premium' | null;
}

export interface InsurerOrgLedgerTotals {
  total_entries: number;
  outstanding_amount_cents: number;
  invoiced_amount_cents: number;
  paid_amount_cents: number;
  void_amount_cents: number;
}

export interface InsurerOrgClientSummary {
  id: number;
  email: string;
  full_name?: string | null;
  is_registered: boolean;
  credits_remaining: number;
  exercises_used: number;
  created_at: string;
}

export interface InsurerOrgTeamMember {
  id: number;
  email: string;
  full_name?: string | null;
  org_role?: string | null;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
}

export interface PartnerLedgerAuditEntry {
  id: number;
  ledger_id: number;
  admin_email: string;
  old_status?: string | null;
  new_status?: string | null;
  old_invoice_reference?: string | null;
  new_invoice_reference?: string | null;
  note?: string | null;
  created_at: string;
}

export interface InsurerOrgLedgerEntry {
  id: number;
  client_email: string;
  client_name?: string | null;
  amount_cents: number;
  status: string;
  invoice_reference?: string | null;
  created_at: string;
  purchase_id: number;
}

export interface InsurerOrgDetail {
  org: InsurerOrg;
  team: InsurerOrgTeamMember[];
  clients: InsurerOrgClientSummary[];
  ledger: InsurerOrgLedgerEntry[];
  totals: InsurerOrgLedgerTotals;
  audit: PartnerLedgerAuditEntry[];
}
